import React, {
    createContext,
    useState,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import { Hub } from 'aws-amplify/utils';
import { getUserRole, getUserFullName, getUserEmail, hasAuth, getUser, startTokenRefresh, stopTokenRefresh, validateAuth } from 'libraries/amplify.library';


interface UserContextProps {
    userRole: string | null;
    userFullName: string | null;
    userEmail: string | null;
    isUserAuthenticated: boolean;
    setUserRole: (role: string | null) => void;
    setUserFullName: (fullName: string | null) => void;
    setUserEmail: (email: string | null) => void;
    setIsUserAuthenticated: (isAuthenticated: boolean) => void;
}

interface UserProviderProps {
    children: React.ReactNode;
}

const userContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [userRole, setUserRole] = useState<string | null>(null);
    const [userFullName, setUserFullName] = useState<string | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [isUserAuthenticated, setIsUserAuthenticated] = useState<boolean>(false);

    // Initialize user data and start token refresh if authenticated
    useEffect(() => {
        const initializeUser = async () => {
            const role = getUserRole();
            const fullName = getUserFullName();
            const email = getUserEmail();
            
            // Use validateAuth for proper token validation (checks expiry)
            const authStatus = await validateAuth();
            
            setUserRole(role);
            setUserFullName(fullName);
            setUserEmail(email);
            setIsUserAuthenticated(authStatus);
            
            // Start token refresh if user is already authenticated
            if (authStatus) {
                //console.log('UserContext - User authenticated with valid token, starting refresh cycle');
                startTokenRefresh();
            } else if (hasAuth()) {
                // Had tokens but validation failed (expired/invalid)
                //console.log('UserContext - Token validation failed, treating as unauthenticated');
            }
        };
        
        initializeUser();
        
        // Cleanup: stop token refresh when component unmounts
        return () => {
            stopTokenRefresh();
        };
    }, []);

    // Listen for authentication events
    useEffect(() => {
        const hubListener = Hub.listen('auth', (data) => {
            const { event } = data.payload;
            //console.log('UserContext - Hub Event:', event);

            switch (event) {
                case 'signInWithRedirect':
                case 'signedIn': {
                    // User signed in - update context
                    const role = getUserRole();
                    const fullName = getUserFullName();
                    const email = getUserEmail();
                    
                    const user = getUser();
                    
                    //console.log('UserContext - User signed in:', { role, fullName, email, user });
                    
                    setUserRole(role);
                    setUserEmail(email);
                    setUserFullName(fullName);
                    setIsUserAuthenticated(true);
                    break;
                }
                case 'signedOut': {
                    // User signed out - clear context
                    //console.log('UserContext - User signed out');
                    setUserRole(null);
                    setUserFullName(null);
                    setUserEmail(null);
                    setIsUserAuthenticated(false);
                    break;
                }
                case 'tokenRefresh': {
                    // Token refreshed - verify auth status
                    const authStatus = hasAuth();
                    setIsUserAuthenticated(authStatus);
                    break;
                }
                case 'tokenRefresh_failure': {
                    // Token refresh failed - user may need to re-authenticate
                    //console.warn('UserContext - Token refresh failed');
                    const authStatus = hasAuth();
                    setIsUserAuthenticated(authStatus);
                    break;
                }
            }
        });

        return () => hubListener();
    }, []);



    const contextValue = useMemo(
        () => ({
            userRole,
            setUserRole,
            userFullName,
            setUserFullName,
            userEmail,
            setUserEmail,
            isUserAuthenticated,
            setIsUserAuthenticated,
        }),
        [userRole, userFullName, userEmail, isUserAuthenticated]
    );


    return (
        <userContext.Provider value={contextValue}>{children}</userContext.Provider>
    );


}


export const useUser = (): UserContextProps => {
    const context = useContext(userContext);
    if (!context) {
        throw new Error('useUser must be within a UserProvider');
    }
    return context;
};